import React from 'react';

export interface FieldHighlight {
  fieldName: string;
  page: number;
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  matchStatus?: 'match' | 'mismatch' | 'pending';
}

interface PdfHighlightOverlayProps {
  highlights: FieldHighlight[];
  currentPage: number;
  pageWidth: number;
  pageHeight: number;
  scale?: number;
  onClick?: (fieldName: string) => void;
}

const PdfHighlightOverlay: React.FC<PdfHighlightOverlayProps> = ({
  highlights,
  currentPage,
  pageWidth,
  pageHeight,
  scale = 1,
  onClick
}) => {
  // Filter highlights for current page
  const pageHighlights = highlights.filter(h => h.page === currentPage);

  if (pageHighlights.length === 0) {
    return null;
  }

  const getStrokeColor = (matchStatus?: 'match' | 'mismatch' | 'pending') => {
    switch (matchStatus) {
      case 'match':
        return '#10b981'; // Green
      case 'mismatch':
        return '#ef4444'; // Red
      case 'pending':
      default:
        return '#f59e0b'; // Orange/yellow
    }
  };

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: pageWidth,
        height: pageHeight,
        pointerEvents: 'none'
      }}
    >
      {pageHighlights.map((highlight, index) => {
        // Scale coordinates to match the current zoom level
        // Add small padding for better visibility
        const pixelPadding = 6; // pixels to expand on each side
        const padding = 0.1; // 10% expansion for width/height

        const paddedWidth = highlight.coordinates.width * (1 + padding);
        const paddedHeight = highlight.coordinates.height * (1 + padding);

        const scaledX = (highlight.coordinates.x * scale) - pixelPadding;
        const scaledY = (highlight.coordinates.y * scale) - pixelPadding;
        const scaledWidth = (paddedWidth * scale) + (pixelPadding * 2);
        const scaledHeight = (paddedHeight * scale) + (pixelPadding * 2);

        const strokeColor = getStrokeColor(highlight.matchStatus);

        return (
          <rect
            key={`${highlight.fieldName}-${index}`}
            x={scaledX}
            y={scaledY}
            width={scaledWidth}
            height={scaledHeight}
            fill={strokeColor}
            fillOpacity="0.1"
            stroke={strokeColor}
            strokeWidth="2"
            style={{
              pointerEvents: onClick ? 'auto' : 'none',
              cursor: onClick ? 'pointer' : 'default'
            }}
            onClick={() => onClick && onClick(highlight.fieldName)}
          />
        );
      })}
    </svg>
  );
};

export default PdfHighlightOverlay;
